import { Outlet, useLocation } from "react-router-dom";
import { AppNavLink, useAppNavigate } from "../lib/route-primitives";
import { useTranslation } from "react-i18next";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useAuth } from "../contexts/auth-context";
import { useWedding } from "../contexts/wedding-context";
import { formatWeddingDate } from "@wedding/shared";
import { useActiveEvent } from "../lib/wedding-event";
import { useActivityBadge } from "../hooks/use-activity-badge";
import { useActivityWs } from "../hooks/use-activity-ws";
import { useTheme } from "../hooks/use-theme";
import { ActivityToastContainer } from "./activity-toast";
import { GuestUserMenu } from "./guest-user-menu";
import {
  Sparkles, Heart, Camera, Image, MonitorPlay,
  Bell, X, MessageCircle, Upload, Play, Loader2, Sun, Moon,
} from "lucide-react";
import { cn } from "../lib/utils";
import { cdnUrl } from "../lib/cdn";
import { paths } from "../lib/paths";
import type { ActivityPayload } from "@wedding/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getActivity, createApi, markSeen, type ActivityItem } from "../lib/api";
import { queryKeys } from "../lib/query-keys";
import { staleTimes } from "../lib/query-client";

interface ToastEntry extends ActivityPayload {
  key: number;
}

// ─── Activity helpers ─────────────────────────────────────────
interface ActivityGroup {
  key: string;
  type: ActivityItem["type"];
  guest_id: string;
  guest_name: string;
  created_at: string;
  items: ActivityItem[];
}

const TEN_MINUTES_MS = 10 * 60 * 1000;

function groupActivityItems(raw: ActivityItem[]): ActivityGroup[] {
  const groups: ActivityGroup[] = [];
  for (const item of raw) {
    const last = groups[groups.length - 1];
    const sameGuest = last?.guest_id === item.guest_id;
    const sameType  = last?.type === item.type;
    const withinWindow =
      last &&
      Math.abs(
        new Date(last.created_at).getTime() - new Date(item.created_at).getTime()
      ) <= TEN_MINUTES_MS;

    if (last && sameGuest && sameType && withinWindow) {
      last.items.push(item);
    } else {
      groups.push({
        key: item.id,
        type: item.type,
        guest_id: item.guest_id,
        guest_name: item.guest_name,
        created_at: item.created_at,
        items: [item],
      });
    }
  }
  return groups;
}

function relativeTime(iso: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins   = Math.floor(diffMs / 60_000);
  if (mins < 1)   return t("activity.justNow");
  if (mins < 60)  return t("activity.minutesAgo", { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return t("activity.hoursAgo", { count: hrs });
  return t("activity.daysAgo", { count: Math.floor(hrs / 24) });
}

// ─── ActivityFeed ─────────────────────────────────────────────
function ActivityFeed({
  slug,
  guestId,
  onOpenLightbox,
  refreshSignal,
}: {
  slug: string;
  guestId?: string;
  onOpenLightbox: (mediaId: string) => void;
  refreshSignal?: number;
}) {
  const { t } = useTranslation();
  const [items, setItems]       = useState<ActivityItem[]>([]);
  const [groups, setGroups]     = useState<ActivityGroup[]>([]);
  const [hasMore, setHasMore]   = useState(false);
  const [loading, setLoading]   = useState(false);
  const [newCount, setNewCount] = useState(0);
  const newestSeenAt            = useRef<string | null>(null);
  const cursorRef               = useRef<string | undefined>(undefined);
  const loadingRef              = useRef(false);

  const fetchFeed = useCallback(async (reset = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const c = reset ? undefined : cursorRef.current;
      const res = await getActivity(slug, "all", c);
      const next = res.data;
      setItems(prev => {
        const merged = reset ? next : [...prev, ...next];
        setGroups(groupActivityItems(merged));
        return merged;
      });
      setHasMore(res.hasMore);
      if (next.length > 0) {
        cursorRef.current = next[next.length - 1].created_at;
        if (reset) newestSeenAt.current = next[0]?.created_at ?? null;
      }
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    setItems([]);
    cursorRef.current = undefined;
    setNewCount(0);
    newestSeenAt.current = null;
    fetchFeed(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Auto-refresh when Layout signals a new WS activity while panel is open
  useEffect(() => {
    if (!refreshSignal) return;
    fetchFeed(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  useEffect(() => {
    async function checkNew() {
      if (!newestSeenAt.current) return;
      try {
        const res = await getActivity(slug, "all");
        if (res.data.length > 0 && res.data[0].created_at > newestSeenAt.current) {
          const count = res.data.filter(i => i.created_at > newestSeenAt.current!).length;
          setNewCount(count);
        }
      } catch { /* ignore */ }
    }
    const interval = setInterval(checkNew, 60_000);
    const onVisible = () => { if (document.visibilityState === "visible") checkNew(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [slug]);

  const iconConfig = {
    like:           { icon: <Heart className="h-4 w-4" />,         bg: "bg-rose-100 dark:bg-rose-500/20 text-rose-500" },
    comment:        { icon: <MessageCircle className="h-4 w-4" />, bg: "bg-sky-100 dark:bg-sky-500/20 text-sky-500" },
    upload:         { icon: <Upload className="h-4 w-4" />,        bg: "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-500" },
    upload_pending: { icon: <Upload className="h-4 w-4" />,        bg: "bg-amber-100 dark:bg-amber-500/20 text-amber-500" },
  };

  function actionText(group: ActivityGroup): string {
    const n = group.items.length;
    const isMe = !!guestId && group.guest_id === guestId;
    const ctx = group.items[0]?.media_kind === "video" ? "video" : undefined;
    switch (group.type) {
      case "like":
        if (isMe) return n === 1 ? t("activity.youLikedOne", { context: ctx }) : t("activity.youLikedMany", { count: n, context: ctx });
        return n === 1 ? t("activity.likedOne", { context: ctx }) : t("activity.likedMany", { count: n, context: ctx });
      case "comment":
        if (isMe) {
          if (n === 1 && group.items[0].content)
            return `${t("activity.youCommentedOne")}: "${group.items[0].content}"`;
          return t("activity.youCommentedMany", { count: n });
        }
        if (n === 1 && group.items[0].content)
          return `${t("activity.commentedOne")}: "${group.items[0].content}"`;
        return t("activity.commentedMany", { count: n });
      case "upload":
        if (isMe) return n === 1 ? t("activity.youUploadedOne", { context: ctx }) : t("activity.youUploadedMany", { count: n, context: ctx });
        return n === 1 ? t("activity.uploadedOne", { context: ctx }) : t("activity.uploadedMany", { count: n, context: ctx });
      case "upload_pending":
        return n === 1 ? t("activity.pendingOne") : t("activity.pendingMany", { count: n });
    }
  }

  return (
    <div className="py-2 space-y-1">
      {newCount > 0 && (
        <button
          onClick={() => { setNewCount(0); fetchFeed(true); }}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-primary/30 bg-primary/5 py-2 text-xs font-medium text-primary transition-colors hover:bg-primary/10 cursor-pointer"
        >
          ↑ {t("activity.newActivity", { count: newCount })}
        </button>
      )}

      {loading && items.length === 0 ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : groups.length === 0 ? (
        <div className="py-10 text-center text-muted-foreground">
          <Bell className="mx-auto mb-3 h-8 w-8 opacity-30" />
          <p className="text-sm">{t("activity.empty")}</p>
          <p className="text-xs mt-1 opacity-70">{t("activity.emptyHint")}</p>
        </div>
      ) : (
        groups.map(group => {
          const cfg = iconConfig[group.type];
          const thumb = group.items.find(i => i.media_r2_key);
          const isPending = group.type === "upload_pending";
          const isMe = !!guestId && group.guest_id === guestId;
          return (
            <button
              key={group.key}
              type="button"
              onClick={thumb && !isPending ? () => onOpenLightbox(thumb.media_id) : undefined}
              disabled={!thumb || isPending}
              className={cn(
                "flex w-full gap-3 px-4 py-2.5 rounded-lg hover:bg-primary/5 transition-colors text-left",
                thumb && !isPending ? "cursor-pointer" : "cursor-default"
              )}
            >
              {/* Type icon */}
              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-primary/20 ${cfg.bg}`}>
                {cfg.icon}
              </div>
              {/* Text */}
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground dark:text-slate-300 leading-tight">
                  {!isPending && !isMe && (
                    <span className="font-bold text-foreground dark:text-white">{group.guest_name}{" "}</span>
                  )}
                  {isMe && (
                    <span className="font-bold text-primary">{t("activity.you")}{" "}</span>
                  )}
                  {actionText(group)}
                </p>
                <p className="text-[10px] text-primary mt-1">{relativeTime(group.created_at, t)}</p>
              </div>
              {/* Thumbnail — single, to the right */}
              {thumb && !isPending && (
                <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0">
                  {thumb.media_kind === "image" ? (
                    <img src={cdnUrl(thumb.media_r2_key, { w: 480 })} alt="" className="h-full w-full object-cover" loading="lazy" />
                  ) : (
                    <div className="relative h-full w-full bg-zinc-900">
                      <video src={`/cdn/${thumb.media_r2_key}`} className="h-full w-full object-cover" muted playsInline preload="metadata" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Play className="h-3 w-3 text-white/80" />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </button>
          );
        })
      )}

      {hasMore && (
        <div className="flex justify-center pt-1">
          <button
            onClick={() => fetchFeed(false)}
            disabled={loading}
            className="flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-xs font-medium hover:bg-muted transition-colors disabled:opacity-50 cursor-pointer"
          >
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {t("common.loadMore")}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Theme toggle (pill switch) ───────────────────────────────
function ThemeToggle({ isDark, toggle }: { isDark: boolean; toggle: () => void }) {
  const { t } = useTranslation();
  const label = isDark ? t("common.switchToLight") : t("common.switchToDark");
  return (
    <button
      onClick={toggle}
      role="switch"
      aria-checked={isDark}
      aria-label={label}
      title={label}
      className={cn(
        "relative h-6 w-11 rounded-full border-2 transition-[background-color,border-color,opacity,transform] duration-300 cursor-pointer shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 hover:opacity-80 hover:scale-105 motion-reduce:hover:scale-100",
        isDark ? "bg-primary/30 border-primary/50" : "bg-amber-200 border-amber-300"
      )}
    >
      <span
        className={cn(
          "absolute top-0.5 h-4 w-4 rounded-full shadow-sm transition-transform duration-300 flex items-center justify-center",
          isDark ? "translate-x-[18px] bg-primary" : "translate-x-0.5 bg-amber-400"
        )}
      >
        {isDark
          ? <Moon className="h-2.5 w-2.5 text-white" />
          : <Sun className="h-2.5 w-2.5 text-white" />
        }
      </span>
    </button>
  );
}

export function Layout() {
  const { t, i18n } = useTranslation();
  const { guest } = useAuth();
  const { slug, wedding } = useWedding();
  // Başlıktaki tarih satırı en yakın etkinliği gösterir (kına → sonra düğün).
  const activeEvent = useActiveEvent(wedding);
  const location = useLocation();
  const navigate = useAppNavigate();
  const {
    galleryUnread, activityUnread,
    incrementGallery, incrementActivity,
    resetGallery, resetActivity,
  } = useActivityBadge();
  const { isDark, toggle: toggleTheme } = useTheme();

  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [refreshSignal, setRefreshSignal] = useState(0);
  const toastKey = useCallback(() => Date.now(), []);
  const panelOpenRef = useRef(false);
  panelOpenRef.current = panelOpen;

  const queryClient = useQueryClient();
  const weddingApi = useMemo(() => createApi(slug), [slug]);

  const { data: rsvpResult } = useQuery({
    queryKey: queryKeys.wedding.rsvp(slug),
    queryFn: () => weddingApi.get<{ status?: string } | null>("/rsvp"),
    staleTime: staleTimes.rsvp,
    enabled: !!slug,
  });

  const rsvpAnswered = !!(rsvpResult?.data?.status);

  const isSlideshow = location.pathname.includes("/slideshow");
  const isSlideshowRef = useRef(false);
  isSlideshowRef.current = isSlideshow;

  const handleActivity = useCallback(
    (payload: ActivityPayload) => {
      incrementGallery();
      incrementActivity();

      // Invalidate home page activity + gallery media so both refresh on next focus/visit
      void queryClient.invalidateQueries({ queryKey: queryKeys.wedding.activity(slug) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.media.all(slug) });

      if (!isSlideshowRef.current) {
        setToasts((prev) => [...prev, { ...payload, key: toastKey() }]);
      }

      if (panelOpenRef.current) {
        setRefreshSignal((s) => s + 1);
      }
    },
    [toastKey, incrementGallery, incrementActivity, queryClient, slug]
  );

  const galleryDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activityDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const markGallerySeen = useCallback(() => {
    if (galleryUnread <= 0) return;
    if (galleryDebounceRef.current) clearTimeout(galleryDebounceRef.current);
    galleryDebounceRef.current = setTimeout(() => {
      markSeen(slug, "gallery", new Date().toISOString());
      resetGallery();
    }, 2000);
  }, [slug, galleryUnread, resetGallery]);

  const markActivitySeen = useCallback(() => {
    if (activityUnread <= 0) return;
    if (activityDebounceRef.current) clearTimeout(activityDebounceRef.current);
    activityDebounceRef.current = setTimeout(() => {
      markSeen(slug, "activity", new Date().toISOString());
      resetActivity();
    }, 2000);
  }, [slug, activityUnread, resetActivity]);

  // On the slideshow route the page mounts its own activity WS (all events, for
  // floating reactions) and toasts are suppressed — so Layout's WS is redundant.
  // Skip it there to avoid a duplicate connection (each WS reconnect costs KV).
  useActivityWs(slug, guest?.id, handleActivity, { enabled: !isSlideshow });

  const dismissToast = useCallback((key: number) => {
    setToasts((prev) => prev.filter((t) => t.key !== key));
  }, []);

  const openPanel = useCallback(() => {
    markActivitySeen();
    setPanelOpen(true);
  }, [markActivitySeen]);

  // ESC key closes panel
  useEffect(() => {
    if (!panelOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setPanelOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [panelOpen]);

  const navItems = [
    { to: paths.wedding.home(slug), icon: Sparkles, label: t("nav.home"), end: true, badge: 0 },
    { to: paths.wedding.rsvp(slug), icon: Heart, label: t("nav.rsvp"), end: true, badge: rsvpAnswered ? 0 : -1 },
    { to: paths.wedding.upload(slug), icon: Camera, label: t("nav.upload"), badge: 0, isFab: true },
    { to: paths.wedding.gallery(slug), icon: Image, label: t("nav.gallery"), badge: 0 },
    { to: paths.wedding.slideshow(slug), icon: MonitorPlay, label: t("nav.slideshow"), badge: 0 },
  ];



  // Close panel when navigating
  useEffect(() => { setPanelOpen(false); }, [location.pathname]);

  return (
    <div className="relative min-h-screen md:flex md:overflow-hidden md:h-screen">
      {/* ── Desktop Sidebar ── */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-primary/10 bg-background sticky top-0 h-screen overflow-y-auto">
        <div className="px-6 py-7 border-b border-primary/10">
          {wedding?.title ? (
            <>
              <p className="font-serif text-2xl font-bold text-foreground leading-tight">
                {wedding.title}
              </p>
              {activeEvent && (
                <p className="mt-1.5 text-xs font-medium text-primary/70 tracking-wide">
                  {formatWeddingDate(activeEvent.dateTime, i18n.language)}
                </p>
              )}
            </>
          ) : (
            <div className="flex items-center gap-3">
              <div className="bg-primary rounded-full p-1.5 flex items-center justify-center text-white shrink-0">
                <Heart className="h-4 w-4" />
              </div>
              <p className="text-primary text-sm font-bold uppercase tracking-wide">
                {t("web.appTitle")}
              </p>
            </div>
          )}
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <AppNavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors relative",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-primary/5 hover:text-primary"
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span className="relative">
                    <item.icon className="h-4 w-4" />
                    {item.badge > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white leading-none">
                        {item.badge > 9 ? "9+" : item.badge}
                      </span>
                    )}
                    {item.badge === -1 && (
                      <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-red-500" />
                    )}
                  </span>
                  <span>{item.label}</span>
                </>
              )}
            </AppNavLink>
          ))}
        </nav>

      </aside>

      {/* ── Mobile Fixed Header ── */}
      <header className="glass fixed top-0 left-0 right-0 z-50 h-16 border-b border-primary/10 md:hidden">
        <div className="flex items-center justify-between h-full px-4">
          <h1 className="font-serif text-xl italic font-bold text-primary truncate">
            {wedding?.title ?? t("web.appTitle")}
          </h1>
          <div className="flex items-center gap-1 shrink-0">
            <ThemeToggle isDark={isDark} toggle={toggleTheme} />
            <button
              onClick={openPanel}
              className="relative p-2 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-full transition-colors cursor-pointer"
              aria-label={t("web.notifications")}
              title={t("web.notifications")}
            >
              <Bell className="h-5 w-5" />
              {activityUnread > 0 && (
                <span className="absolute top-2 right-2 w-2 h-2 bg-primary rounded-full border-2 border-background" />
              )}
            </button>
            <GuestUserMenu />
          </div>
        </div>
      </header>

      {/* ── Desktop Sticky Header + Content Column ── */}
      <div className="flex-1 flex flex-col md:overflow-y-auto md:h-screen">
        <header className="hidden md:flex sticky top-0 z-10 items-center justify-end px-8 py-4 bg-background/80 backdrop-blur-md border-b border-primary/5 shrink-0 gap-2">
          <ThemeToggle isDark={isDark} toggle={toggleTheme} />
          <button
            onClick={openPanel}
            className="relative p-2 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-full transition-colors cursor-pointer"
            aria-label={t("web.notifications")}
            title={t("web.notifications")}
          >
            <Bell className="h-5 w-5" />
            {activityUnread > 0 && (
              <span className="absolute top-2 right-2 w-2 h-2 bg-primary rounded-full border-2 border-background" />
            )}
          </button>
          <GuestUserMenu />
        </header>

        <main className="flex-1 px-4 pt-20 pb-24 md:px-8 md:py-8 overflow-x-clip">
          <div className="mx-auto w-full max-w-7xl">
            <Outlet context={{ galleryUnread, markGallerySeen, markActivitySeen }} />
          </div>
        </main>
      </div>

      {/* ── Mobile Fixed Bottom Nav ── */}
      <nav className="glass fixed bottom-0 left-0 right-0 z-50 border-t border-primary/10 md:hidden">
        <div className="flex items-center justify-around px-2 pt-2 pb-6">
          {navItems.map((item) => {
            if (item.isFab) {
              return (
                <AppNavLink
                  key={item.to}
                  to={item.to}
                  className="flex flex-col items-center gap-1 -mt-8"
                >
                  <div className="w-14 h-14 bg-primary rounded-full flex items-center justify-center shadow-lg shadow-primary/40 border-4 border-white dark:border-[#1b1817]">
                    <Camera className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-tighter text-muted-foreground mt-1">
                    {item.label}
                  </span>
                </AppNavLink>
              );
            }
            return (
              <AppNavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    "flex flex-col items-center gap-1 py-1 px-2 transition-colors",
                    isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  )
                }
              >
                <span className="relative">
                  <item.icon className="h-5 w-5" />
                  {item.badge > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white leading-none">
                      {item.badge > 9 ? "9+" : item.badge}
                    </span>
                  )}
                  {item.badge === -1 && (
                    <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-red-500" />
                  )}
                </span>
                <span className="text-[10px] font-bold uppercase tracking-tighter">{item.label}</span>
              </AppNavLink>
            );
          })}
        </div>
      </nav>

      {/* Activity Toasts */}
      <ActivityToastContainer toasts={toasts} slug={slug} onDismiss={dismissToast} />

      {/* ── Notification Dropdown ── */}
      {panelOpen && (
        <div className="fixed inset-0 z-[60]" aria-hidden="true" onClick={() => setPanelOpen(false)} />
      )}
      <div
        className={cn(
          "fixed top-16 right-2 md:right-8 z-[70]",
          "w-[calc(100%-1rem)] max-w-sm",
          "flex flex-col",
          // Light: solid card. Dark: glass panel matching template.
          "bg-card dark:bg-[rgba(27,24,23,0.82)] dark:backdrop-blur-xl",
          "border border-border dark:border-primary/20",
          "rounded-2xl shadow-2xl overflow-hidden",
          "transition-all duration-200 origin-top-right",
          panelOpen
            ? "opacity-100 scale-100 pointer-events-auto"
            : "opacity-0 scale-95 pointer-events-none"
        )}
      >
        {/* Panel header */}
        <div className="p-4 flex items-center justify-between border-b border-primary/10 shrink-0">
          <h3 className="font-serif text-lg font-bold">{t("web.notifications")}</h3>
          <button
            onClick={() => setPanelOpen(false)}
            className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            aria-label={t("common.close")}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="max-h-96 overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-primary/30 [&::-webkit-scrollbar-thumb:hover]:bg-primary/60 [&::-webkit-scrollbar-thumb]:rounded-full">
          {panelOpen && (
            <ActivityFeed
              slug={slug}
              guestId={guest?.id}
              refreshSignal={refreshSignal}
              onOpenLightbox={(mediaId) => {
                setPanelOpen(false);
                navigate(paths.wedding.gallery(slug), { state: { openMediaId: mediaId } });
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
